// A row's time, three ways: how long ago, as a glance down the list; and exactly, in the reader's own
// time zone and in UTC, on hover and in the row's detail. The log spans venues in many time zones, so
// no venue's zone is right for the whole of it — the reader's is the one they think in, and UTC is the
// one a support call and the trace viewer agree on. The window's own instants stay UTC (`filters.ts`).

import { createVenueFormatter, type VenueFormatter } from '@clavian/i18n';
import { useVenueFormatter } from '@clavian/i18n/react';
import { useMemo } from 'react';

const EXACT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'short',
};

const SHORT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'short',
};

export interface AuditTimes {
  /** "12 minutes ago". */
  relative(instant: string, now?: number): string;
  /** "22 Sept 2026, 14:00:00 GMT+5:30", in the reader's time zone. */
  local(instant: string): string;
  /** "22 Sept 2026, 08:30:00 UTC". */
  utc(instant: string): string;
  /** "22 Sept 2026, 08:30 UTC": a window's edge, on its chip. */
  utcShort(instant: string): string;
}

/** The reader's own time zone, as the browser reports it. */
const readerTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export function useAuditTimes(): AuditTimes {
  const format = useVenueFormatter();
  return useMemo(() => {
    const local: VenueFormatter = createVenueFormatter({ ...format.venue, timeZone: readerTimeZone() }, { locale: format.locale });
    const utc: VenueFormatter = createVenueFormatter({ ...format.venue, timeZone: 'UTC' }, { locale: format.locale });
    return {
      relative: (instant, now = Date.now()) => local.relative(instant, now),
      local: (instant) => local.custom(instant, EXACT),
      utc: (instant) => utc.custom(instant, EXACT),
      utcShort: (instant) => utc.custom(instant, SHORT),
    };
  }, [format]);
}
